import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { login } from "../../api/auth";
import { useAuth } from "../../context/AuthContext";
import {
  Mail,
  Lock,
  Eye,
  EyeOff,
  Loader2,
  AlertCircle,
  LayoutDashboard,
  Images,
  FileText,
  ShieldCheck,
  ArrowUpRight,
  Globe,
} from "lucide-react";

const FEATURES = [
  {
    icon: LayoutDashboard,
    title: "Tek panelden yönetim",
    text: "Web sitenizin tüm içeriğini tek yerden güncelleyin.",
  },
  {
    icon: FileText,
    title: "İçerik ve sayfa yönetimi",
    text: "Metinlerinizi, duyurularınızı ve sayfalarınızı kolayca düzenleyin.",
  },
  {
    icon: Images,
    title: "Galeri ve görseller",
    text: "Fotoğraf ve görsellerinizi sürükle-bırak ile yönetin.",
  },
  {
    icon: ShieldCheck,
    title: "Güvenli erişim",
    text: "Yetkilendirme tabanlı, güvenli oturum altyapısı.",
  },
];

export default function LoginPage() {
  const navigate = useNavigate();
  const { login: authLogin } = useAuth();
  const [mail, setMail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const data = await login(mail, password);
      if (data?.token) {
        authLogin(data);
        navigate("/", { replace: true });
      }
    } catch (err) {
      setError("E-posta veya şifre hatalı. Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      {/* Sol: marka paneli */}
      <aside className="login-brand">
        <div className="login-brand-glow" />

        <div className="login-brand-top">
          <a
            href="https://softana.com.tr"
            target="_blank"
            rel="noopener noreferrer"
            className="login-brand-logo"
          >
            <img src="/images/logo-white.png" alt="Softana" />
          </a>
        </div>

        <div className="login-brand-content">
          <span className="login-brand-badge">Yönetim Paneli</span>
          <h1>
            Kurumsal web sitenizi
            <br />
            kolayca yönetin.
          </h1>
          <p className="login-brand-sub">
            Softana müşteri paneli ile web sitenizin içeriklerini teknik bilgiye
            ihtiyaç duymadan güncelleyin.
          </p>

          <ul className="login-features">
            {FEATURES.map(({ icon: Icon, title, text }) => (
              <li key={title}>
                <span className="login-feature-icon">
                  <Icon size={18} />
                </span>
                <div>
                  <strong>{title}</strong>
                  <span>{text}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="login-brand-bottom">
          <a
            href="https://softana.com.tr"
            target="_blank"
            rel="noopener noreferrer"
            className="login-cta"
          >
            <span className="login-cta-icon">
              <Globe size={18} />
            </span>
            <span className="login-cta-text">
              <strong>Web sitesi mi yaptırmak istiyorsunuz?</strong>
              <span>Kurumsal web çözümleri için softana.com.tr</span>
            </span>
            <ArrowUpRight size={18} className="login-cta-arrow" />
          </a>
        </div>
      </aside>

      {/* Sağ: giriş formu */}
      <main className="login-panel">
        <div className="login-box">
          <div className="login-box-header">
            <img
              src="/images/logo-black.png"
              alt="Softana"
              className="login-box-logo"
            />
            <h2>Panele giriş yapın</h2>
            <p>Devam etmek için hesap bilgilerinizi girin.</p>
          </div>

          {error && (
            <div className="login-error" role="alert">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form" noValidate>
            <div className="form-field">
              <label htmlFor="mail">E-posta Adresi</label>
              <div className="input-wrapper">
                <Mail size={18} className="input-icon" />
                <input
                  id="mail"
                  type="email"
                  value={mail}
                  onChange={(e) => setMail(e.target.value)}
                  placeholder="ornek@sirketiniz.com"
                  required
                  autoComplete="email"
                  autoFocus
                />
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="password">Şifre</label>
              <div className="input-wrapper">
                <Lock size={18} className="input-icon" />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="input-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={-1}
                  aria-label={showPassword ? "Şifreyi gizle" : "Şifreyi göster"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button type="submit" className="login-btn" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 size={18} className="spin" />
                  Giriş yapılıyor...
                </>
              ) : (
                "Giriş Yap"
              )}
            </button>
          </form>

          <p className="login-help">
            Giriş bilgilerinizi mi unuttunuz?{" "}
            <a href="https://softana.com.tr" target="_blank" rel="noopener noreferrer">
              Bizimle iletişime geçin
            </a>
          </p>
        </div>

        <footer className="login-footer">
          <p>
            © {new Date().getFullYear()}{" "}
            <a href="https://softana.com.tr" target="_blank" rel="noopener noreferrer">
              Softana
            </a>{" "}
            — Tüm hakları saklıdır.
          </p>
        </footer>
      </main>
    </div>
  );
}
